import axios from 'axios';

import { APP_ERROR_MESSAGES } from '@/config/app-error-messages';

type JsonRecord = Record<string, unknown>;

const ERROR_KEYS = ['message', 'error', 'detail', 'title'] as const;

const isRecord = (value: unknown): value is JsonRecord => typeof value === 'object' && value !== null;

export const getErrorMessage = (error: unknown, fallbackMessage = 'Something went wrong'): string => {
    const isNetworkError = (axiosError: unknown): boolean => {
        if (!axios.isAxiosError(axiosError)) {
            return false;
        }

        const normalizedMessage = axiosError.message?.trim().toLowerCase();
        return (
            axiosError.code === 'ERR_NETWORK' ||
            (!axiosError.response && (normalizedMessage === 'network error' || normalizedMessage === 'failed to fetch'))
        );
    };

    const isTimeoutError = (axiosError: unknown): boolean => {
        if (!axios.isAxiosError(axiosError)) {
            return false;
        }

        const normalizedMessage = axiosError.message?.trim().toLowerCase() ?? '';
        return axiosError.code === 'ECONNABORTED' || normalizedMessage.includes('timeout');
    };

    const extractFromUnknown = (value: unknown, visited: WeakSet<object> = new WeakSet()): string | null => {
        if (typeof value === 'string') {
            const trimmed = value.trim();
            return trimmed.length > 0 ? trimmed : null;
        }

        if (Array.isArray(value)) {
            if (visited.has(value)) {
                return null;
            }
            visited.add(value);
            return (
                value.map((item) => extractFromUnknown(item, visited)).find((extracted): extracted is string => Boolean(extracted)) ?? null
            );
        }

        if (!isRecord(value)) {
            return null;
        }

        if (visited.has(value)) {
            return null;
        }
        visited.add(value);

        const prioritizedMessage =
            ERROR_KEYS.map((key) => value[key])
                .map((item) => extractFromUnknown(item, visited))
                .find((extracted): extracted is string => Boolean(extracted)) ?? null;

        if (prioritizedMessage) {
            return prioritizedMessage;
        }

        return (
            Object.values(value)
                .map((item) => extractFromUnknown(item, visited))
                .find((extracted): extracted is string => Boolean(extracted)) ?? null
        );
    };

    if (axios.isAxiosError(error)) {
        if (isTimeoutError(error)) {
            return APP_ERROR_MESSAGES.REQUEST_TIMEOUT;
        }

        if (isNetworkError(error)) {
            return fallbackMessage;
        }

        const responseMessage = extractFromUnknown(error.response?.data);
        if (responseMessage) {
            return responseMessage;
        }

        const requestMessage = extractFromUnknown(error.message);
        if (requestMessage) {
            return requestMessage;
        }

        return fallbackMessage;
    }

    const extracted = extractFromUnknown(error);
    return extracted ?? fallbackMessage;
};
